import { useMemo, useRef, useState } from "react"
import {
  Archive,
  ArrowCounterClockwise,
  CalendarBlank,
  Copy,
  DotsThree,
  FolderMinus,
  PencilSimple,
  Trash,
  TShirt,
} from "@phosphor-icons/react"

import {
  Badge,
  Button,
  DateRangePicker,
  EmptyState,
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  IconButton,
  InlineAlert,
  Input,
  PermissionTooltip,
  RadioGroup,
  RadioGroupItem,
} from "~/components/design-system/components"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import { Tooltip } from "~/components/reusable/Tooltip"
import { usePermission } from "~/hooks/usePermission"
import { useVariants } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { pluralize } from "~/utils/formatting"
import {
  DateFormatType,
  differenceInCalendarDaysUTC,
  formatDate,
  toUTCDateString,
} from "~/utils/datetime"
import { PACKAGE_NAME_MAX, packageName } from "~/utils/allocationPackageOps"
import { matchesLineSearch, VARIANT_SEARCH_OPTIONS } from "~/utils/lineSearch"
import { showErrorToast } from "~/utils/toaster"
import {
  formatDayShift,
  formatPackageDate,
  OVERLAP_ADVICE,
  OVERLAP_TITLE,
  overlapSummary,
  overlapTooltip,
  PACKAGE_STATUS_META,
  PACKAGE_TYPE_LABEL,
} from "./packageDisplay"
import { CardTitle, PACKAGE_CARD_KEY } from "./PackageCardTitle"
import { DeletionImpact, folderPath } from "./PackageFolderPage"
import { PackageStoresCard, useStoresCardExpanded } from "./PackageStoresCard"
import { noteRunAfterCurrent, usePackageStoresDraft } from "./setup/setupDrafts"
import {
  ARCHIVED_BY_FOLDER_REASON,
  isArchivedByFolder,
  pageDisabledReason,
} from "./packagePageGating"
import { type PackageFolder, type RangePackage } from "./types"
import { useAllocationMutations } from "./useAllocationData"
import { useLineSearch } from "./useLineSearch"

const PACKAGE_TYPE_BADGE = {
  noos: "emphasizedBlue",
  seasonal: "violet",
  clearance: "neutral",
} as const satisfies Record<RangePackage["type"], string>
const PACKAGE_RANGE_SEARCH_ID = "allocation-package-range"

type SavePackagePage = ReturnType<typeof useAllocationMutations>["savePackagePage"]

function copyName(name: string, taken: Set<string>) {
  const base = `${name.trim()} (copy)`
  if (!taken.has(base.toLowerCase())) return base
  let n = 2
  while (taken.has(`${name.trim()} (copy ${n})`.toLowerCase())) n++
  return `${name.trim()} (copy ${n})`
}

function PackageName({
  pkg,
  nameRef,
  disabledReason,
  save,
}: {
  pkg: RangePackage
  nameRef: React.RefObject<HTMLInputElement | null>
  disabledReason: string | undefined
  save: SavePackagePage
}) {
  const [draft, setDraft] = useState(pkg.name)

  const commit = () => {
    if (draft.trim() === pkg.name.trim()) return setDraft(pkg.name)
    const parsed = packageName.safeParse(draft)
    if (!parsed.success) {
      showErrorToast(new Error(parsed.error.issues[0]?.message))
      return setDraft(pkg.name)
    }
    const name = parsed.data
    save.mutate(
      { op: { kind: "renamePackage", packageId: pkg.id, name } },
      {
        onError: (error) => {
          setDraft(pkg.name)
          showErrorToast(error)
        },
      },
    )
  }

  return (
    <Input
      ref={nameRef}
      variant="transparent"
      aria-label="Package name"
      className="header-h2! field-sizing-content max-w-full rounded-md px-1.5"
      containerClassName="min-w-0"
      value={draft}
      maxLength={PACKAGE_NAME_MAX}
      requires="edit"
      disabled={disabledReason !== undefined}
      allowedTitle={disabledReason}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur()
        if (event.key === "Escape") {
          const input = event.currentTarget
          setDraft(pkg.name)
          requestAnimationFrame(() => input.blur())
        }
      }}
    />
  )
}

function PackageActionsMenu({
  pkg,
  packages,
  folders,
  nameRef,
  disabledReason,
  save,
  onDuplicated,
  onDelete,
}: {
  pkg: RangePackage
  packages: RangePackage[]
  folders: PackageFolder[]
  nameRef: React.RefObject<HTMLInputElement | null>
  disabledReason: string | undefined
  save: SavePackagePage
  onDuplicated: (packageId: string) => void
  onDelete: () => void
}) {
  const draft = usePackageStoresDraft()
  const deleting = draft.isDeleting(pkg.id)
  const archivedByFolder = isArchivedByFolder(pkg, folders)
  const busy = save.isPending || draft.saving
  const page = (op: Parameters<SavePackagePage["mutate"]>[0]["op"]) =>
    save.mutate(
      { op },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  const duplicate = () => {
    const taken = new Set(packages.map((p) => p.name.trim().toLowerCase()))
    save.mutate(
      {
        op: {
          kind: "duplicatePackage",
          packageId: crypto.randomUUID(),
          sourceId: pkg.id,
          name: copyName(pkg.name, taken),
        },
      },
      { onSuccess: ({ packageId }) => onDuplicated(packageId), onError: showErrorToast },
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger requires="edit" allowedTitle="More actions" asChild>
        <IconButton variant="transparent" size="sm" aria-label="More actions">
          <DotsThree size={16} weight="bold" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={disabledReason !== undefined}
          onSelect={() =>
            requestAnimationFrame(() => {
              nameRef.current?.focus()
              nameRef.current?.select()
            })
          }
        >
          <PencilSimple size={16} />
          <span className="flex flex-col">
            Rename…
            {disabledReason && <span className="paragraph-sm text-fg-muted">{disabledReason}</span>}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={busy} onSelect={duplicate}>
          <Copy size={16} />
          Duplicate package
        </DropdownMenuItem>
        {pkg.folderId !== null && (
          <DropdownMenuItem
            disabled={busy || disabledReason !== undefined}
            onSelect={() =>
              page({ kind: "movePackage", packageId: pkg.id, folderId: null, stores: "keep" })
            }
          >
            <FolderMinus size={16} />
            Move out of folder
          </DropdownMenuItem>
        )}
        {pkg.ownDeactivated ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => page({ kind: "setPackageActive", packageId: pkg.id, active: true })}
          >
            <ArrowCounterClockwise size={16} />
            Restore package
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={busy || archivedByFolder}
            onSelect={() => page({ kind: "setPackageActive", packageId: pkg.id, active: false })}
          >
            <Archive size={16} />
            <span className="flex flex-col">
              Archive package
              {archivedByFolder && (
                <span className="paragraph-sm text-fg-muted">{ARCHIVED_BY_FOLDER_REASON}</span>
              )}
            </span>
          </DropdownMenuItem>
        )}
        {deleting ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => draft.unmarkForDeletion({ packageId: pkg.id })}
          >
            <ArrowCounterClockwise size={16} />
            Keep package
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={busy} onSelect={onDelete} className="text-fg-negative-text">
            <Trash size={16} />
            Delete package…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PackageHeader(props: {
  pkg: RangePackage
  packages: RangePackage[]
  folders: PackageFolder[]
  disabledReason: string | undefined
  save: SavePackagePage
  onDuplicated: (packageId: string) => void
  onDelete: () => void
  onSelectFolder: (folderId: string) => void
}) {
  const { pkg, folders, disabledReason, save, onSelectFolder, ...menuProps } = props
  const nameRef = useRef<HTMLInputElement>(null)
  const status = PACKAGE_STATUS_META[pkg.status]
  const deleting = usePackageStoresDraft().isDeleting(pkg.id)
  const path = folderPath(folders, pkg.folderId)
  return (
    <div className="flex flex-col gap-1">
      {path.length > 0 && (
        <nav aria-label="Folder" className="flex items-center gap-1">
          {path.map((folder) => (
            <Button
              key={folder.id}
              variant="transparent"
              size="sm"
              onClick={() => onSelectFolder(folder.id)}
            >
              {folder.name} ›
            </Button>
          ))}
        </nav>
      )}
      <div className="flex items-center gap-3">
        <PackageName
          key={pkg.name}
          pkg={pkg}
          nameRef={nameRef}
          disabledReason={disabledReason}
          save={save}
        />
        <Badge variant={PACKAGE_TYPE_BADGE[pkg.type]}>{PACKAGE_TYPE_LABEL[pkg.type]}</Badge>
        <Badge variant={status.variant}>{status.label}</Badge>
        {deleting && <span className="label-sm text-fg-muted">· deleting on save</span>}
        <span className="label-sm text-fg-muted">
          {pluralize(pkg.productIds.length, "variant")} ·{" "}
          {pluralize(pkg.skuIds?.length ?? 0, "SKU")}
        </span>
        <div className="flex-1" />
        <PackageActionsMenu
          {...menuProps}
          pkg={pkg}
          folders={folders}
          disabledReason={disabledReason}
          save={save}
          nameRef={nameRef}
        />
      </div>
    </div>
  )
}

type DateBound = "start" | "cutoff"
const OWN_DATES_CHOICES = ["shift", "keep"] as const
type OwnDatesChoice = (typeof OWN_DATES_CHOICES)[number]
const isOwnDatesChoice = (value: string): value is OwnDatesChoice =>
  (OWN_DATES_CHOICES as readonly string[]).includes(value)
type Window = { startDate: string; cutoffDate: string }

type PendingWindow = {
  window: Window
  bounds: DateBound[]
  step: number
  choices: Record<DateBound, OwnDatesChoice>
}

const toDate = (day: string) => new Date(`${day}T00:00:00Z`)

const arrowWindowLabel = (window: Window) =>
  `${formatPackageDate(window.startDate)} → ${formatPackageDate(window.cutoffDate)}`

function dateFieldLabel(date: Date) {
  return formatDate(date, DateFormatType.FullDateShort)
}

function ownDatesHint(pkg: RangePackage) {
  if (pkg.ownDatesVariantCount === 0) return "Every variant in this package follows these dates."
  const own = pluralize(pkg.ownDatesVariantCount, "variant")
  return `Changing these dates moves every variant that follows the package. ${own} with their own dates can be shifted along or kept.`
}

function OwnDatesConfirmation({
  pkg,
  current,
  pending,
  saving,
  onChoose,
  onCancel,
  onApply,
}: {
  pkg: RangePackage
  current: Window
  pending: PendingWindow
  saving: boolean
  onChoose: (choice: OwnDatesChoice) => void
  onCancel: () => void
  onApply: () => void
}) {
  const bound = pending.bounds[pending.step]!
  const isStart = bound === "start"
  const from = isStart ? current.startDate : current.cutoffDate
  const to = isStart ? pending.window.startDate : pending.window.cutoffDate
  const days = differenceInCalendarDaysUTC(toDate(to), toDate(from))
  const followers = pkg.productIds.length - pkg.ownDatesVariantCount
  const own = pkg.ownDatesVariantCount
  const boundLabel = isStart ? "start" : "replenishment cut-off"
  const isLast = pending.step === pending.bounds.length - 1

  return (
    <InlineAlert
      tone="warning"
      size="block"
      title={`Move the package ${boundLabel} to ${formatPackageDate(to)}?`}
      description={
        <div className="flex flex-col gap-2">
          <span>
            {pluralize(followers, "variant")} {followers === 1 ? "follows" : "follow"} the package
            and {followers === 1 ? "moves" : "move"} with it. {pluralize(own, "variant")}{" "}
            {own === 1 ? "has" : "have"} dates set by hand:
          </span>
          <RadioGroup
            value={pending.choices[bound]}
            onValueChange={(value) => {
              if (isOwnDatesChoice(value)) onChoose(value)
            }}
          >
            <label className="flex items-start gap-2">
              <RadioGroupItem value="shift" className="mt-0.5" />
              <span className="flex flex-col">
                <span className="label-base text-fg-base">
                  Shift them too ({formatDayShift(days)})
                </span>
                <span className="paragraph-sm text-fg-muted">
                  Their own {isStart ? "start" : "cut-off"} dates move by the same amount.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <RadioGroupItem value="keep" className="mt-0.5" />
              <span className="flex flex-col">
                <span className="label-base text-fg-base">Keep their own dates</span>
                <span className="paragraph-sm text-fg-muted">
                  Only variants that follow the package move.
                </span>
              </span>
            </label>
          </RadioGroup>
        </div>
      }
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{
        label: isLast ? "Apply" : "Next",
        onClick: onApply,
        requires: "edit",
        disabled: saving,
      }}
    />
  )
}

function DatesSource({
  pkg,
  folders,
  current,
  disabled,
  save,
}: {
  pkg: RangePackage
  folders: PackageFolder[]
  current: Window | null
  disabled: boolean
  save: SavePackagePage
}) {
  if (pkg.type === "noos" || pkg.folderId === null) return null
  const followed = folders.find((folder) => folder.id === pkg.datesFolderId)
  const folder = folders.find((other) => other.id === pkg.folderId)
  const setWindow = (window: Window | null) =>
    save.mutate(
      {
        op: {
          kind: "setPackageWindow",
          packageId: pkg.id,
          window,
          shiftOwnStart: false,
          shiftOwnCutoff: false,
        },
      },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  if (followed) {
    return (
      <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
        <Badge variant="neutral">From {followed.name}</Badge>
        <Button
          variant="transparent"
          size="sm"
          requires="edit"
          allowedTitle="Give this package its own dates, starting from the folder's"
          disabled={disabled || current === null}
          onClick={() => setWindow(current)}
        >
          Use own dates
        </Button>
      </div>
    )
  }
  if (pkg.startDate === null) return null
  return (
    <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
      <Badge variant="violet">Own dates</Badge>
      <Button
        variant="transparent"
        size="sm"
        requires="edit"
        allowedTitle="Follow the folder's dates again"
        disabled={disabled}
        onClick={() => setWindow(null)}
      >
        {`Use ${folder?.name ?? "folder"}'s dates`}
      </Button>
    </div>
  )
}

function datesMeta(pkg: RangePackage, current: Window | null) {
  if (current) return arrowWindowLabel(current)
  return pkg.type === "noos" ? "Always open" : "No dates"
}

function PackageDatesCard({
  pkg,
  folders,
  disabledReason,
  save,
}: {
  pkg: RangePackage
  folders: PackageFolder[]
  disabledReason: string | undefined
  save: SavePackagePage
}) {
  const [pending, setPending] = useState<PendingWindow | null>(null)
  const { allowed } = usePermission("edit")
  const following = pkg.datesFolderId !== null
  const current =
    pkg.startDate && pkg.cutoffDate
      ? { startDate: pkg.startDate, cutoffDate: pkg.cutoffDate }
      : null

  const commit = (window: Window, shiftOwnStart: boolean, shiftOwnCutoff: boolean) =>
    save.mutate(
      {
        op: { kind: "setPackageWindow", packageId: pkg.id, window, shiftOwnStart, shiftOwnCutoff },
      },
      { onSuccess: () => setPending(null), onError: showErrorToast },
    )

  const onPick = (from: Date | undefined, to: Date | undefined) => {
    if (!from || !to || !current) return
    const window = { startDate: toUTCDateString(from), cutoffDate: toUTCDateString(to) }
    const bounds: DateBound[] = []
    if (window.startDate !== current.startDate) bounds.push("start")
    if (window.cutoffDate !== current.cutoffDate) bounds.push("cutoff")
    if (bounds.length === 0) return
    if (pkg.ownDatesVariantCount === 0) return commit(window, false, false)
    setPending({ window, bounds, step: 0, choices: { start: "shift", cutoff: "shift" } })
  }

  const advance = () => {
    if (!pending) return
    if (pending.step < pending.bounds.length - 1) {
      return setPending({ ...pending, step: pending.step + 1 })
    }
    const shifts = (bound: DateBound) =>
      pending.bounds.includes(bound) && pending.choices[bound] === "shift"
    commit(pending.window, shifts("start"), shifts("cutoff"))
  }

  const weeks = current
    ? Math.max(
        1,
        Math.round(
          differenceInCalendarDaysUTC(toDate(current.cutoffDate), toDate(current.startDate)) / 7,
        ),
      )
    : 0

  return (
    <ExpandableCard className="shrink-0" persistKey={`${PACKAGE_CARD_KEY}.dates`} defaultExpanded>
      <ExpandableCardHeader>
        <CardTitle meta={datesMeta(pkg, current)}>Dates</CardTitle>
        <DatesSource
          pkg={pkg}
          folders={folders}
          current={current}
          disabled={disabledReason !== undefined || save.isPending}
          save={save}
        />
      </ExpandableCardHeader>
      <ExpandableCardContent className="flex flex-col gap-3">
        {pkg.type === "noos" ? (
          <p className="paragraph-sm text-fg-muted">
            Always open. A NOOS package has no start date or replenishment cut-off; it stays open
            until it is archived.
          </p>
        ) : current ? (
          <>
            <div className="flex items-center gap-3">
              <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
                <div className="w-[280px]">
                  <DateRangePicker
                    startDate={toDate((pending?.window ?? current).startDate)}
                    endDate={toDate((pending?.window ?? current).cutoffDate)}
                    onApply={({ from, to }) => onPick(from, to)}
                    formatDate={dateFieldLabel}
                    validateRange={(from, to) =>
                      from && to ? null : "Pick a start date and a replenishment cut-off."
                    }
                    align="start"
                    disabled={
                      !allowed || disabledReason !== undefined || pending !== null || following
                    }
                  />
                </div>
              </PermissionTooltip>
              <span className="label-sm text-fg-muted">
                {pluralize(weeks, "week")} · start date to replenishment cut-off
              </span>
            </div>
            {following && (
              <p className="paragraph-sm text-fg-muted">
                {"Follows the folder's dates. Use own dates to set dates for this package only."}
              </p>
            )}
            {!following && pending ? (
              <OwnDatesConfirmation
                pkg={pkg}
                current={current}
                pending={pending}
                saving={save.isPending}
                onChoose={(choice) =>
                  setPending({
                    ...pending,
                    choices: { ...pending.choices, [pending.bounds[pending.step]!]: choice },
                  })
                }
                onCancel={() => setPending(null)}
                onApply={advance}
              />
            ) : (
              !following && <p className="paragraph-sm text-fg-muted">{ownDatesHint(pkg)}</p>
            )}
          </>
        ) : (
          <>
            <EmptyState
              icon={CalendarBlank}
              title="When does this package sell?"
              description="Nothing replenishes until the package has dates. Seasonal packages need a start date and a replenishment cut-off."
            />
            <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
              <div className="mx-auto w-[280px]">
                <DateRangePicker
                  startDate={undefined}
                  endDate={undefined}
                  onApply={({ from, to }) => {
                    if (from && to) {
                      commit(
                        { startDate: toUTCDateString(from), cutoffDate: toUTCDateString(to) },
                        false,
                        false,
                      )
                    }
                  }}
                  formatDate={dateFieldLabel}
                  validateRange={(from, to) =>
                    from && to ? null : "Pick a start date and a replenishment cut-off."
                  }
                  align="start"
                  disabled={!allowed || disabledReason !== undefined || save.isPending}
                />
              </div>
            </PermissionTooltip>
          </>
        )}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

function getProductsForPackage(pkg: RangePackage, variants: IVariantSummary[]) {
  const ids = new Set(pkg.productIds)
  return variants
    .filter((v) => ids.has(v.variantId))
    .map((v) => ({
      id: v.variantId,
      name: v.variantDisplayName,
      category: v.category ?? "Other",
      variant: v,
    }))
}

function PackageVariantsCard({
  pkg,
  disabledReason,
  onEditRange,
}: {
  pkg: RangePackage
  disabledReason: string | undefined
  onEditRange: () => void
}) {
  const variants = useVariants()
  const products = useMemo(() => getProductsForPackage(pkg, variants), [pkg, variants])
  const { search, setSearch, searchBy, setSearchBy } = useLineSearch(
    PACKAGE_RANGE_SEARCH_ID,
    VARIANT_SEARCH_OPTIONS,
  )

  const filtered = useMemo(
    () => products.filter((p) => matchesLineSearch(p, search, searchBy, (row) => row.variant)),
    [products, search, searchBy],
  )

  const grouped = useMemo(() => Map.groupBy(filtered, (p) => p.category), [filtered])
  const editRange = {
    requires: "edit" as const,
    disabled: disabledReason !== undefined,
    allowedTitle: disabledReason,
  }

  return (
    <ExpandableCard
      className="shrink-0"
      persistKey={`${PACKAGE_CARD_KEY}.variants`}
      defaultExpanded
    >
      <ExpandableCardHeader>
        <CardTitle meta={pluralize(pkg.productIds.length, "variant")}>Variants</CardTitle>
        <div className="flex-1" />
        {pkg.productIds.length > 0 && (
          <>
            <ExpandableSearchField
              initialSearchText={search}
              onSearchTextChange={setSearch}
              placeholder="Search..."
              debounceTime={200}
              searchBy={searchBy}
              onSearchByChange={setSearchBy}
              searchByOptions={VARIANT_SEARCH_OPTIONS}
            />
            <Button
              variant="transparent"
              size="xs"
              className="gap-1"
              {...editRange}
              onClick={onEditRange}
            >
              <PencilSimple size={12} />
              Edit range
            </Button>
          </>
        )}
      </ExpandableCardHeader>
      <ExpandableCardContent>
        {pkg.productIds.length === 0 ? (
          <EmptyState
            icon={TShirt}
            title="Your variants will show here"
            description="This is where you choose what the package sells. Pick variants from the product master."
            action={{ label: "Add variants", onClick: onEditRange, ...editRange }}
          />
        ) : (
          <div className="space-y-4">
            {[...grouped.entries()].map(([category, items]) => (
              <div key={category}>
                <div className="mb-1.5 flex items-baseline gap-2">
                  <h4 className="text-xs font-semibold text-fg-base">{category}</h4>
                  <span className="text-[11px] text-fg-subtle">
                    {pluralize(items.length, "variant")}
                  </span>
                </div>
                <div className="space-y-1">
                  {items.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-2 rounded-md border border-border-base px-3 py-1.5"
                    >
                      <span className="flex-1 text-sm text-fg-base">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {grouped.size === 0 && (
              <p className="text-sm text-fg-subtle">
                {products.length === 0
                  ? "Variant details are not available yet."
                  : "No variants match your search."}
              </p>
            )}
          </div>
        )}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

function OverlapBar({
  overlap,
  disabledReason,
  onOpenStores,
  onEditRange,
}: {
  overlap: NonNullable<RangePackage["overlap"]>
  disabledReason: string | undefined
  onOpenStores: () => void
  onEditRange: () => void
}) {
  const text = `${overlapSummary(overlap)} ${OVERLAP_ADVICE}`
  return (
    <InlineAlert
      tone="warning"
      className="shrink-0 flex-nowrap"
      description={
        <Tooltip title={overlapTooltip(overlap, true)}>
          <span className="block truncate">
            <span className="label-sm text-fg-base">{OVERLAP_TITLE}</span> {text}
          </span>
        </Tooltip>
      }
      secondaryAction={{
        label: "Edit variants",
        onClick: onEditRange,
        requires: "edit",
        disabled: disabledReason !== undefined,
        allowedTitle: disabledReason,
      }}
      action={{ label: "Open stores", onClick: onOpenStores }}
    />
  )
}

export function PackagePage({
  pkg,
  packages,
  folders,
  onEditRange,
  onSelectPackage,
  onSelectFolder,
}: {
  pkg: RangePackage
  packages: RangePackage[]
  folders: PackageFolder[]
  onEditRange: () => void
  onSelectPackage: (packageId: string) => void
  onSelectFolder: (folderId: string) => void
}) {
  const { savePackagePage } = useAllocationMutations()
  const draft = usePackageStoresDraft()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const deleting = draft.isDeleting(pkg.id)
  const disabledReason = pageDisabledReason(pkg, folders, deleting)
  const storesRef = useRef<HTMLDivElement>(null)
  const [, setStoresExpanded] = useStoresCardExpanded(pkg)
  const openStores = () => {
    setStoresExpanded(true)
    requestAnimationFrame(() =>
      storesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    )
  }
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
      <PackageHeader
        pkg={pkg}
        packages={packages}
        folders={folders}
        disabledReason={disabledReason}
        save={savePackagePage}
        onDuplicated={onSelectPackage}
        onDelete={() => setConfirmDelete(true)}
        onSelectFolder={onSelectFolder}
      />
      {confirmDelete && !deleting && (
        <DeletionImpact
          target={{ packageId: pkg.id }}
          label={pkg.name}
          onCancel={() => setConfirmDelete(false)}
          onMarked={() => setConfirmDelete(false)}
        />
      )}
      {pkg.overlap && (
        <OverlapBar
          overlap={pkg.overlap}
          disabledReason={disabledReason}
          onOpenStores={openStores}
          onEditRange={onEditRange}
        />
      )}
      <PackageDatesCard
        pkg={pkg}
        folders={folders}
        disabledReason={disabledReason}
        save={savePackagePage}
      />
      <PackageVariantsCard pkg={pkg} disabledReason={disabledReason} onEditRange={onEditRange} />
      <div ref={storesRef} className="shrink-0 scroll-mt-6">
        <PackageStoresCard pkg={pkg} folders={folders} disabledReason={disabledReason} />
      </div>
    </div>
  )
}
